import { useToast } from '@repo/design-system/index'
import { QIcon_ai_speaking_partner, QIcon_chevron_right } from '@repo/icons/index'
import classNames from 'classnames'
import produce from 'immer'
import { useContext, useEffect, useState } from 'react'
import Slider from 'react-slick'
import { useEventTracking } from '../../../../helpers/gtmHelper'
import apis from '../../../../modules/apis'
import { useMaterials } from '../../../../modules/apis/student/material/queries'
import { axiosPost } from '../../../../modules/axiosHelper'
import { APP_SCREEN_NAME, STACK_ACTIVITY_NAME } from '../../../../modules/hooks/common/appRouterVars'
import useAppRouter from '../../../../modules/hooks/common/useAppRouter'
import useIsMobile from '../../../../modules/hooks/common/useIsMobile'
import useReturnFullUrl from '../../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_ai_speaking, n_common, s_portal_home } from '../../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../../modules/i18n/useTranslation'
import urls from '../../../../modules/urls'
import { COLORS } from '../../../../modules/vars'
import { SelectMaterialContext } from '../../../../pages/[lang]/portal/lessons/materials/[lessonId]'
import { useAITutorStack } from '../../../../pages/[lang]/webview/ai-tutor'
import { useConversationAIStack } from '../../../../pages/[lang]/webview/conversational-ai'
import { useLessonsStack } from '../../../../pages/[lang]/webview/lessons'
import { useMaterialsStack } from '../../../../pages/[lang]/webview/materials'
import SliderNextBtn from '../../../common/icons/SliderNextBtn'
import SliderPrevBtn from '../../../common/icons/SliderPrevBtn'
import SelectMaterialCard, { SelectMaterialCardSkeleton } from './SelectMaterialCard'

interface IRecommendedListSection {
  setCategoryId
  isList?: boolean
  isConversation?: boolean | { [x: string]: boolean }
  isAITutor?: boolean
}

const getSkeletonArray = (isList) => (isList ? [0, 1, 2, 3] : [0, 1, 2])

export const ConversationCourseSelectInstruction = () => {
  const { t } = useTranslation()
  return (
    <div className="flex flex-row px-[12px] py-[12px] bg-gray-100 rounded-[4px]">
      <QIcon_ai_speaking_partner className="h-[20px] w-[20px] mr-[8px]" color={COLORS.PURPLE500} />
      <div className="Body2-14Bd text-gray-700">{t(n_ai_speaking.material.detail)}</div>
    </div>
  )
}

const RecommendedListSection = ({
  setCategoryId,
  isList = false,
  isConversation = false,
  isAITutor = false,
}: IRecommendedListSection) => {
  const { t } = useTranslation()
  const { toastSuccess } = useToast()
  const router = useAppRouter()
  const { sendClickEvent } = useEventTracking()
  const getFullUrl = useReturnFullUrl()
  const { useLessonsFlow } = useLessonsStack()
  const { push: lessonPush } = useLessonsFlow()
  const lessonRouter = useAppRouter({ stackPush: lessonPush })

  const { useMaterialsFlow } = useMaterialsStack()
  const { push: materialsPush } = useMaterialsFlow()
  const materialsRouter = useAppRouter({ stackPush: materialsPush })

  const [lowerHashtags, setLowerHashtags] = useState([])
  const isSmallerLarge = useIsMobile(null, 1040)
  const isSmallerMedium = useIsMobile(null, 768)
  const isSmallerSmall = useIsMobile(null, 640)

  const { selectedCourse, setSelectedCourse, lessonId, threadId } = useContext(SelectMaterialContext)
  const lessonSelectLessonId = lessonId ? lessonId : router.query.lessonId

  const { data: materialsData, isFetching: materialsIsFetching } = useMaterials(isConversation || isAITutor)

  const { useConversationAIFlow } = useConversationAIStack()
  const { push: conversationPush } = useConversationAIFlow()
  const conversationRouter = useAppRouter({ stackPush: conversationPush })

  const { useAITutorFlow } = useAITutorStack()
  const { push: useAITutorPush } = useAITutorFlow()
  const aiTutorRouter = useAppRouter({ stackPush: useAITutorPush })

  const currentThreadId = threadId ? threadId : router.query.threadId

  const handleAllButton = (id) => {
    setCategoryId(id)
    scrollTo(0, 0)
    const newUrl = new URL(window.location.href)
    newUrl.searchParams.set('category', id)
    window.history.pushState({}, '', newUrl)
  }

  useEffect(() => {
    if (materialsData && !materialsIsFetching) {
      if (isConversation || isAITutor) {
        setLowerHashtags([
          materialsData?.lesson_courses,
          materialsData?.favorite_courses,
          ...materialsData.lower_hashtags,
        ])
      } else {
        setLowerHashtags(materialsData.lower_hashtags)
      }
    }
  }, [materialsData, materialsIsFetching])

  const onClickCourseDetail = (course) => {
    if (isConversation) {
      conversationRouter.push('', {
        stackActivity: STACK_ACTIVITY_NAME.CONVERSATION_MATERIAL_DETAIL_ACTIVITY,
        stackSendData: {
          materialId: course.id,
          threadId: currentThreadId,
        },
      })
      return
    }

    if (isAITutor) {
      aiTutorRouter.push(urls.webview.aiTutor.discussion.materialDetail(course.id), {
        appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
        appSendData: {
          url: getFullUrl(urls.webview.aiTutor.discussion.materialDetail(course.id)),
        },
      })
      return
    }

    // 기존 Router Push
    if (!isSmallerSmall) {
      lessonRouter.push(
        isList
          ? urls.student.newPortal.materials.detail(course.id)
          : urls.student.newPortal.materials.detail(course.id) + `?lessonId=${lessonSelectLessonId}`,
        {
          stackActivity: STACK_ACTIVITY_NAME.LESSON_MATERIAL_DETAIL_ACTIVITY,
          stackSendData: {
            materialId: course.id,
            lessonId: lessonSelectLessonId,
          },
        }
      )
    } else {
      router.asPath.includes('webview/materials')
        ? materialsRouter.push(
            isList
              ? urls.student.newPortal.materials.detail(course.id)
              : urls.student.newPortal.materials.detail(course.id) + `?lessonId=${lessonSelectLessonId}`,
            {
              stackActivity: STACK_ACTIVITY_NAME.MATERIAL_DETAIL_ACTIVITY,
              stackSendData: {
                materialId: course.id,
                lessonId: lessonSelectLessonId,
              },
            }
          )
        : lessonRouter.push(
            isList
              ? urls.student.newPortal.materials.detail(course.id)
              : urls.student.newPortal.materials.detail(course.id) + `?lessonId=${lessonSelectLessonId}`,
            {
              appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
              appSendData: {
                url: getFullUrl(urls.webview.lessons.lessonMaterialDetail(lessonSelectLessonId, course.id)),
              },
            }
          )
    }
  }

  const handleLikeToggle = async (event, material) => {
    event.preventDefault()
    event.stopPropagation()

    const response = await sendLikeToggleRequest(material.id)

    if (response?.success) {
      updateIsMineStatus(material.id, response.my_course.is_selected)
      displayLikeToggleToast(response)
    } else {
      console.error(response)
    }
  }

  const sendLikeToggleRequest = async (materialId) => {
    try {
      const result = await axiosPost(apis.likeMaterial(), {
        course_id: materialId,
        material_id: materialId,
      })
      return result
    } catch (error) {
      console.error('Error toggling like status:', error)
      return null
    }
  }

  const updateIsMineStatus = (packetId, isSelected) => {
    const updatedHashtags = produce(lowerHashtags, (draft) => {
      draft.forEach((hashtag) => {
        const course = hashtag.courses.find((course) => course.id === packetId)
        if (course) {
          course.is_mine = isSelected
        }
      })
    })
    setLowerHashtags(updatedHashtags)
  }

  const displayLikeToggleToast = (response) => {
    toastSuccess(
      t(response.my_course.is_selected ? s_portal_home.material_like_added : s_portal_home.material_like_deleted)
    )
  }

  const handleClickSelect = (course) => {
    const isSelected = course.id === selectedCourse?.id

    if (isSelected) {
      setSelectedCourse(undefined)
    } else {
      setSelectedCourse(course)
    }
  }

  const calculateSlidesToShow = () => {
    if (isList) {
      if (isSmallerLarge) {
        return isSmallerMedium ? 2 : 3
      } else {
        return 4
      }
    } else {
      return isSmallerMedium ? 2 : 3
    }
  }

  const onClickCategory = (material) => {
    if (isList) {
      // Fix
      if (isAITutor) {
        aiTutorRouter.push(urls.webview.aiTutor.discussion.materialCategory(material.id), {
          appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
          appSendData: {
            url: getFullUrl(urls.webview.aiTutor.discussion.materialCategory(material.id)),
          },
        })
        return
      }
      router.asPath.includes('webview/materials')
        ? materialsRouter.push(urls.student.newPortal.materials.category(material.id), {
            stackActivity: STACK_ACTIVITY_NAME.MATERIAL_CATEGORY_ACTIVITY,
            stackSendData: {
              categoryId: material.id,
            },
          })
        : lessonRouter.push(urls.student.newPortal.materials.category(material.id), {
            appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
            appSendData: {
              url: getFullUrl(urls.webview.lessons.lessonMaterialsCategory(material.id)),
            },
          })
    } else {
      handleAllButton(material.id)
    }
  }

  return (
    <div className={classNames(isList ? 'lg:ml-[0px]' : ' lg:ml-[196px] lg:mt-[0px]', 'relative flex flex-col')}>
      {isConversation && (
        <div className="mb-[36px]">
          <ConversationCourseSelectInstruction />
        </div>
      )}
      {lowerHashtags.length > 0 ? (
        lowerHashtags.map((material) => {
          const titleTranslations = [
            material.title_kr,
            material.title_en,
            '',
            material.title_zh_chs,
            material.title_zh_cht,
            material.title_ja,
            material.title_vi,
          ]

          if ((isConversation || isAITutor) && material.courses.length == 0) {
            return <></>
          }

          return (
            <div key={material.id} className="mb-[42px] w-full">
              <div className="mb-[14px] flex flex-row justify-between lg:pr-[8px] pt-[8px]">
                <div className="Title1-20Bd">{t(titleTranslations)}</div>
                {!material.filter && (
                  <button className="flex flex-row items-center gap-[2px]" onClick={() => onClickCategory(material)}>
                    <div className="Body1-16Bd hidden text-gray-500 sm:block">{t(n_common.btn_all)}</div>
                    <QIcon_chevron_right width={16} height={16} color={COLORS.GRAY500} className="mt-[2px]" />
                  </button>
                )}
              </div>
              <section className="mt-5">
                {!isSmallerSmall ? (
                  <div className={classNames(isList ? 'lg:w-max-[1040px]' : 'lg:w-[688px]', 'w-full')}>
                    <Slider
                      dots={false}
                      infinite={false}
                      slidesToShow={calculateSlidesToShow()}
                      slidesToScroll={isSmallerMedium ? 2 : 3}
                      draggable={false}
                      lazyLoad={'ondemand'}
                      nextArrow={<SliderNextBtn />}
                      prevArrow={<SliderPrevBtn />}
                      className="slick-no-center"
                    >
                      {material.courses?.slice(0, 12).map((course) => (
                        <div
                          className=""
                          key={course.id}
                          onClick={() => {
                            sendClickEvent({ button_name: 'material_card', object_id: course.id })
                            onClickCourseDetail(course)
                          }}
                        >
                          <SelectMaterialCard
                            selected={course.id === selectedCourse?.id}
                            onClickSelect={() => handleClickSelect(course)}
                            onClickHeart={(e) => handleLikeToggle(e, course)}
                            title={course.title}
                            subtitle={course.subtitle}
                            is_mine={course.is_mine}
                            category={course.category}
                            image_url={course.image_url}
                            is_revised={course.is_revised}
                            is_today={course.is_today}
                            isList={isList}
                            level={course.level}
                            audioLength={course.mp3_running_time_in_minute}
                          />
                        </div>
                      ))}
                    </Slider>
                  </div>
                ) : (
                  <div className="no-scrollbar flex w-full flex-row gap-[12px] overflow-x-scroll sm:grid sm:grid-cols-2 sm:overflow-x-hidden md:grid-cols-3">
                    {material.courses?.map((course) => (
                      <div
                        className=""
                        key={course.id}
                        onClick={() => {
                          sendClickEvent({ button_name: 'material_card', object_id: course.id })
                          onClickCourseDetail(course)
                        }}
                      >
                        <SelectMaterialCard
                          selected={course.id === selectedCourse?.id}
                          onClickSelect={() => handleClickSelect(course)}
                          onClickHeart={(e) => handleLikeToggle(e, course)}
                          title={course.title}
                          subtitle={course.subtitle}
                          is_mine={course.is_mine}
                          category={course.category}
                          image_url={course.image_url}
                          is_revised={course.is_revised}
                          is_today={course.is_today}
                          isList={isList}
                          level={course.level}
                          audioLength={course.mp3_running_time_in_minute}
                        />
                      </div>
                    ))}
                  </div>
                )}
              </section>
            </div>
          )
        })
      ) : (
        <>
          {getSkeletonArray(isList).map((item, i) => {
            return (
              <div className="mb-[32px] w-full" key={i}>
                <div className="mb-[14px]">
                  <div className="h-[28px] w-[200px] rounded-[4px] bg-gray-200"></div>
                </div>
                <section className="mt-5">
                  <div
                    className={classNames(
                      isList ? 'md:grid-cols-4 lg:w-max-[1040px]' : 'md:grid-cols-3 lg:w-[688px]',
                      'flex w-full flex-row gap-[16px] sm:grid sm:grid-cols-2'
                    )}
                  >
                    {getSkeletonArray(isList).map((_, i) => (
                      <div className="w-full" key={i}>
                        <SelectMaterialCardSkeleton />
                      </div>
                    ))}
                  </div>
                </section>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}

export default RecommendedListSection
